import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { verifyEmail, resendVerificationEmail } from '../../services/authService';
import { resendErrorMessage } from '../../components/EmailVerificationPrompt/EmailVerificationPrompt';
import { readStashedToken, clearStashedToken } from '../../utils/sensitiveUrl';
import { readPendingQuiz } from '../../quiz/pendingQuiz';
import { CoinCelebration } from '../../components/CoinCelebration/CoinCelebration';
import { rarityName } from '../../components/PendingCoinCard/PendingCoinCard';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import styles from './VerifyEmail.module.css';

/**
 * Landing page for the link in the verification email:
 *   {APP_WEB_URL}/verify-email?token=...
 *
 * Verification is session-bound: the link proves the inbox, the signed-in
 * session proves the account. A signed-out visitor (e.g. the link opened on
 * another device) signs in first and lands back here; the token waits in this
 * tab's sessionStorage, never in the address bar.
 */
export default function VerifyEmail() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user, loading, updateUserData, refreshOnboardingStatus } = useAuth();
    // The token is moved out of the address bar before React mounts (see
    // utils/sensitiveUrl.js) so analytics never sees it; fall back to the query
    // string only for environments where the scrub did not run.
    const token = readStashedToken('token', '/verify-email') || searchParams.get('token');
    const pendingQuiz = readPendingQuiz(user?.id);

    const [status, setStatus] = useState(token ? 'verifying' : 'missing');
    const [errorMessage, setErrorMessage] = useState(null);
    const [emailChanged, setEmailChanged] = useState(false);
    // A used link reopened by an account that was already verified.
    const [alreadyVerified, setAlreadyVerified] = useState(false);
    const [resendState, setResendState] = useState({ status: 'idle', message: null });
    // The coin the first quiz before verification earned, granted by this
    // verification. The reveal plays once, then the page shows where it went.
    const [claimedCoin, setClaimedCoin] = useState(null);
    const [revealDone, setRevealDone] = useState(false);

    // React 18 StrictMode double-invokes effects in dev; the token is single-use,
    // so guard against firing the request twice.
    const attemptedRef = useRef(false);

    useEffect(() => {
        if (!token || loading || attemptedRef.current) return;
        if (!user) {
            setStatus('signin');
            return;
        }
        attemptedRef.current = true;

        (async () => {
            try {
                const result = await verifyEmail(token);
                clearStashedToken('token', '/verify-email');
                setEmailChanged(Boolean(result?.emailChanged));
                setAlreadyVerified(Boolean(result?.alreadyVerified));
                const claimed = result?.pendingCoin?.status === 'claimed' ? result.pendingCoin : null;
                setClaimedCoin(claimed);
                setStatus('verified');
                updateUserData({ emailVerified: true });
                if (!result?.alreadyVerified) {
                    trackEvent(ANALYTICS_EVENTS.EMAIL_VERIFIED, { platform: 'web', has_pending_coin: !!claimed }, { onceKey: user.id });
                }
                if (claimed) {
                    trackEvent(ANALYTICS_EVENTS.PENDING_COIN_CLAIMED, { platform: 'web', coin_tier: claimed.badge?.coin?.rarity }, { onceKey: user.id });
                    // coin_count and the post-first-coin Watchlist step changed on the server.
                    refreshOnboardingStatus?.();
                }
            } catch (err) {
                // A wrong-account link stays stashed: signing in to the right
                // account (same tab) can still finish it.
                if (err?.code !== 'verification_wrong_account') clearStashedToken('token', '/verify-email');
                setErrorMessage(err?.message || 'This verification link is invalid or has expired.');
                setStatus('failed');
            }
        })();
    }, [token, loading, user]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleResend = async () => {
        if (resendState.status === 'sending') return;
        setResendState({ status: 'sending', message: null });
        try {
            const data = await resendVerificationEmail();
            setResendState(data?.alreadyVerified
                ? { status: 'sent', message: 'Your email is already verified — no new link needed.' }
                : { status: 'sent', message: 'Sent! Check your inbox for a fresh link (it expires in 24 hours).' });
        } catch (err) {
            setResendState({ status: 'error', message: resendErrorMessage(err) });
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.card}>
                {status === 'verifying' && (
                    <>
                        <div className={styles.spinner} />
                        <h1 className={styles.title}>Verifying your email…</h1>
                    </>
                )}

                {status === 'signin' && (
                    <>
                        <span className={styles.icon}>🔐</span>
                        <h1 className={styles.title}>Sign in to finish</h1>
                        <p className={styles.body}>
                            For your security, sign in to the Aluvo account this email is for, and
                            we'll finish verifying it right away. Signed up in the Aluvo app? Use the
                            same email and password here.
                        </p>
                        <Link className={styles.primaryBtn} to="/login" state={{ from: { pathname: '/verify-email' } }}>
                            Sign in
                        </Link>
                        <p className={styles.body}>
                            Can't sign in? <Link to="/forgot-password">Reset your password</Link>.
                        </p>
                    </>
                )}

                {status === 'verified' && emailChanged && (
                    <>
                        <span className={styles.icon}>✅</span>
                        <h1 className={styles.title}>Email updated</h1>
                        <p className={styles.body}>This address is now your Aluvo account email.</p>
                        <button className={styles.primaryBtn} onClick={() => navigate('/settings/account')}>
                            Back to your account
                        </button>
                    </>
                )}

                {status === 'verified' && !emailChanged && claimedCoin && (
                    <>
                        {!revealDone && (
                            <CoinCelebration
                                coinLabel={claimedCoin.badge?.badge
                                    ? `${claimedCoin.badge.badge.label_top ?? ''} · ${claimedCoin.badge.badge.label_bottom ?? ''}`
                                    : null}
                                coinImageUrl={claimedCoin.badge?.coin?.coinImageUrl ?? null}
                                subtitle="✅ Coin claimed!"
                                shareUrl={null}
                                onComplete={() => setRevealDone(true)}
                            />
                        )}
                        <span className={styles.icon}>🪙</span>
                        <h1 className={styles.title}>Email verified — your coin is yours</h1>
                        <p className={styles.body}>
                            Your {rarityName(claimedCoin.badge?.coin?.rarity)} coin is now in your collection. You can take as many challenges as you like.
                        </p>
                        <button className={styles.primaryBtn} onClick={() => navigate('/me')}>
                            See my collection
                        </button>
                    </>
                )}

                {status === 'verified' && !emailChanged && !claimedCoin && (
                    <>
                        <span className={styles.icon}>✅</span>
                        <h1 className={styles.title}>{alreadyVerified ? 'Your email is already verified' : 'Email verified'}</h1>
                        <p className={styles.body}>
                            {pendingQuiz
                                ? `You're all set — let's pick up where you left off${pendingQuiz.mediaTitle ? ` with ${pendingQuiz.mediaTitle}` : ''}.`
                                : "You're all set — you can earn coins now. Go take a challenge on something you've watched."}
                        </p>
                        <button className={styles.primaryBtn} onClick={() => navigate(pendingQuiz?.path || '/discover')}>
                            {pendingQuiz ? 'Continue to your challenge' : 'Start earning coins'}
                        </button>
                        <p className={styles.body}>
                            Using the Aluvo app? Go back to it — your challenge continues there
                            (or tap “I've verified my email”).
                        </p>
                    </>
                )}

                {status === 'missing' && (
                    <>
                        <span className={styles.icon}>🔗</span>
                        <h1 className={styles.title}>No verification link found</h1>
                        <p className={styles.body}>
                            Open the link straight from the email we sent you, or request a
                            new one below.
                        </p>
                        {user && (
                            <button
                                className={styles.primaryBtn}
                                onClick={handleResend}
                                disabled={resendState.status === 'sending'}
                            >
                                {resendState.status === 'sending' ? 'Sending…' : 'Send me a new link'}
                            </button>
                        )}
                    </>
                )}

                {status === 'failed' && (
                    <>
                        <span className={styles.icon}>⌛</span>
                        <h1 className={styles.title}>That link didn't work</h1>
                        <p className={styles.body}>{errorMessage}</p>
                        {user ? (
                            <button
                                className={styles.primaryBtn}
                                onClick={handleResend}
                                disabled={resendState.status === 'sending'}
                            >
                                {resendState.status === 'sending' ? 'Sending…' : 'Send me a new link'}
                            </button>
                        ) : (
                            <Link className={styles.primaryBtn} to="/login">
                                Log in to resend
                            </Link>
                        )}
                    </>
                )}

                {resendState.message && (
                    <p
                        className={resendState.status === 'error' ? styles.feedbackError : styles.feedbackOk}
                        role="status"
                    >
                        {resendState.message}
                    </p>
                )}

                <Link className={styles.browseLink} to="/discover">
                    Keep browsing Aluvo →
                </Link>
            </div>
        </div>
    );
}
